// pages/hr/wizard/steps/TaxFormsStep.tsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  FormControl,
  FormLabel,
  Input,
  Button,
  RadioGroup,
  Radio,
  Checkbox,
  Divider,
  Alert,
} from '@mui/joy';
import { Info as InfoOutlined } from '@phosphor-icons/react';
import type { WizardStepProps } from '../../../../types/hr';

interface TaxFormsData {
  // W-4 fields (for W-2)
  filing_status: 'single' | 'married_filing_jointly' | 'married_filing_separately' | 'head_of_household';
  multiple_jobs: boolean;
  dependents_amount: string;
  other_income: string;
  deductions: string;
  extra_withholding: string;
  exempt_from_withholding: boolean;

  // W-9 fields (for 1099)
  business_name: string;
  tax_classification: 'individual' | 'sole_proprietor' | 'llc' | 'c_corp' | 's_corp' | 'partnership' | 'trust';
  has_tin: boolean;
  tin: string;
  tin_type: 'ssn' | 'ein';

  // State tax
  state_withholding_allowances: string;
  additional_state_withholding: string;
}

export const TaxFormsStep: React.FC<WizardStepProps> = ({
  step,
  sessionState,
  onDataChange,
  onNext,
  onBack,
}) => {
  const isW2 = sessionState.member_type === 'w2';

  const [formData, setFormData] = useState<TaxFormsData>({
    filing_status: 'single',
    multiple_jobs: false,
    dependents_amount: '',
    other_income: '',
    deductions: '',
    extra_withholding: '',
    exempt_from_withholding: false,
    business_name: '',
    tax_classification: 'individual',
    has_tin: true,
    tin: '',
    tin_type: 'ssn',
    state_withholding_allowances: '0',
    additional_state_withholding: '',
    ...((sessionState.step_data[step.id] || {}) as Partial<TaxFormsData>),
  });

  const [errors, setErrors] = useState<Partial<Record<keyof TaxFormsData, string>>>({});

  useEffect(() => {
    onDataChange(formData as unknown as Record<string, unknown>);
  }, [formData]);

  const handleChange = (field: keyof TaxFormsData, value: string | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const validate = (): boolean => {
    const newErrors: Partial<Record<keyof TaxFormsData, string>> = {};

    if (!isW2) {
      // W-9 validation
      if (formData.has_tin && !formData.tin.trim()) {
        newErrors.tin = 'Tax identification number is required';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validate()) {
      onNext();
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography level="h4" sx={{ mb: 1 }}>
        {isW2 ? 'Tax Withholding (W-4)' : 'Tax Information (W-9)'}
      </Typography>
      <Typography level="body-sm" color="neutral" sx={{ mb: 3 }}>
        {isW2
          ? 'Complete your federal tax withholding information. This determines how much tax is withheld from your paycheck.'
          : 'Provide your tax information for 1099 reporting purposes.'}
      </Typography>

      {isW2 ? (
        // W-4 Form for W-2 employees
        <>
          <Alert color="neutral" variant="soft" startDecorator={<InfoOutlined />} sx={{ mb: 3 }}>
            If you're unsure how to complete this form, select "Single" as your filing status and leave other
            fields blank. You can update this information later with HR.
          </Alert>

          {/* Filing Status */}
          <Typography level="title-md" sx={{ mb: 2 }}>
            Step 1: Filing Status
          </Typography>
          <FormControl sx={{ mb: 3 }}>
            <RadioGroup
              value={formData.filing_status}
              onChange={(e) => handleChange('filing_status', e.target.value)}
            >
              <Radio value="single" label="Single or Married filing separately" />
              <Radio value="married_filing_jointly" label="Married filing jointly" />
              <Radio value="head_of_household" label="Head of household" />
            </RadioGroup>
          </FormControl>

          <Divider sx={{ my: 3 }} />

          {/* Multiple Jobs */}
          <Typography level="title-md" sx={{ mb: 2 }}>
            Step 2: Multiple Jobs (Optional)
          </Typography>
          <FormControl sx={{ mb: 3 }}>
            <Checkbox
              checked={formData.multiple_jobs}
              onChange={(e) => handleChange('multiple_jobs', e.target.checked)}
              label="Check if you have more than one job or your spouse also works"
            />
            <Typography level="body-xs" color="neutral" sx={{ ml: 3.5 }}>
              This helps ensure the correct amount of tax is withheld
            </Typography>
          </FormControl>

          <Divider sx={{ my: 3 }} />

          {/* Dependents */}
          <Typography level="title-md" sx={{ mb: 2 }}>
            Step 3: Claim Dependents (Optional)
          </Typography>
          <FormControl sx={{ mb: 3, maxWidth: 300 }}>
            <FormLabel>Total dependent credits</FormLabel>
            <Input
              startDecorator="$"
              value={formData.dependents_amount}
              onChange={(e) => handleChange('dependents_amount', e.target.value)}
              placeholder="0"
            />
            <Typography level="body-xs" color="neutral">
              $2,000 per child under 17, $500 for other dependents
            </Typography>
          </FormControl>

          <Divider sx={{ my: 3 }} />

          {/* Other Adjustments */}
          <Typography level="title-md" sx={{ mb: 2 }}>
            Step 4: Other Adjustments (Optional)
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 3 }}>
            <FormControl>
              <FormLabel>Other income (not from jobs)</FormLabel>
              <Input
                startDecorator="$"
                value={formData.other_income}
                onChange={(e) => handleChange('other_income', e.target.value)}
                placeholder="0"
              />
              <Typography level="body-xs" color="neutral">
                Interest, dividends, retirement income, etc.
              </Typography>
            </FormControl>
            <FormControl>
              <FormLabel>Deductions</FormLabel>
              <Input
                startDecorator="$"
                value={formData.deductions}
                onChange={(e) => handleChange('deductions', e.target.value)}
                placeholder="0"
              />
              <Typography level="body-xs" color="neutral">
                If total exceeds standard deduction
              </Typography>
            </FormControl>
            <FormControl>
              <FormLabel>Extra withholding per pay period</FormLabel>
              <Input
                startDecorator="$"
                value={formData.extra_withholding}
                onChange={(e) => handleChange('extra_withholding', e.target.value)}
                placeholder="0"
              />
              <Typography level="body-xs" color="neutral">
                Additional amount to withhold
              </Typography>
            </FormControl>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Exempt */}
          <FormControl sx={{ mb: 3 }}>
            <Checkbox
              checked={formData.exempt_from_withholding}
              onChange={(e) => handleChange('exempt_from_withholding', e.target.checked)}
              label="I claim exemption from withholding"
            />
            <Typography level="body-xs" color="neutral" sx={{ ml: 3.5 }}>
              Only claim this if you had no tax liability last year and expect none this year
            </Typography>
          </FormControl>
        </>
      ) : (
        // W-9 Form for 1099 contractors
        <>
          <Typography level="title-md" sx={{ mb: 2 }}>
            Business Information
          </Typography>
          <FormControl sx={{ mb: 3 }}>
            <FormLabel>Business Name (if different from your name)</FormLabel>
            <Input
              value={formData.business_name}
              onChange={(e) => handleChange('business_name', e.target.value)}
              placeholder="Optional business name"
            />
          </FormControl>

          <FormControl sx={{ mb: 3 }}>
            <FormLabel>Tax Classification</FormLabel>
            <RadioGroup
              value={formData.tax_classification}
              onChange={(e) => handleChange('tax_classification', e.target.value)}
            >
              <Radio value="individual" label="Individual/sole proprietor" />
              <Radio value="llc" label="Limited Liability Company (LLC)" />
              <Radio value="c_corp" label="C Corporation" />
              <Radio value="s_corp" label="S Corporation" />
              <Radio value="partnership" label="Partnership" />
              <Radio value="trust" label="Trust/estate" />
            </RadioGroup>
          </FormControl>

          <Divider sx={{ my: 3 }} />

          <Typography level="title-md" sx={{ mb: 2 }}>
            Taxpayer Identification Number
          </Typography>
          <FormControl sx={{ mb: 2 }}>
            <RadioGroup
              orientation="horizontal"
              value={formData.tin_type}
              onChange={(e) => handleChange('tin_type', e.target.value)}
              sx={{ flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 1, sm: 2 } }}
            >
              <Radio value="ssn" label="Social Security Number (SSN)" />
              <Radio value="ein" label="Employer Identification Number (EIN)" />
            </RadioGroup>
          </FormControl>
          <FormControl error={!!errors.tin} sx={{ maxWidth: 300 }}>
            <FormLabel>{formData.tin_type === 'ssn' ? 'SSN' : 'EIN'} *</FormLabel>
            <Input
              value={formData.tin}
              onChange={(e) => handleChange('tin', e.target.value.replace(/\D/g, ''))}
              placeholder={formData.tin_type === 'ssn' ? 'XXX-XX-XXXX' : 'XX-XXXXXXX'}
            />
            {errors.tin && (
              <Typography level="body-xs" color="danger">
                {errors.tin}
              </Typography>
            )}
          </FormControl>

          <Alert color="warning" variant="soft" sx={{ mt: 3 }}>
            Under penalties of perjury, you certify that the information provided is correct and you are a U.S.
            person.
          </Alert>
        </>
      )}

      {/* Navigation */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
        <Button variant="outlined" color="neutral" onClick={onBack}>
          Back
        </Button>
        <Button onClick={handleSubmit}>Continue</Button>
      </Box>
    </Box>
  );
};

export default TaxFormsStep;
